import { useState, useEffect } from 'react';
import { Button } from '../../navigation/Button';
import { Icon } from '../../display/Icon';
import { css } from '@emotion/react';
import { Animation, Colors, Shadows, Typography } from '../../../theme';
import { isolateComponent, labelStyles, withModifier, } from '@zapier/style-encapsulation';
const Styles = labelStyles('Accordion', {
    root: (derivedState) => [
        css `
      position: relative;
      background-color: ${Colors.neutral200};
      border: 1px solid ${Colors.neutral200};
      border-radius: 10px;
      transition-property: box-shadow, border-color, background-color;
      transition-timing-function: ${Animation.transitionTimingFunction};
      transition-duration: ${Animation.transitionDuration};
    `,
        (derivedState.isFocused || derivedState.isHovered || derivedState.isOpen) &&
            withModifier('highlighted', css `
          background-color: ${Colors.neutral100};
          border-color: ${Colors.neutral400};
          border-radius: 10px;
          box-shadow: ${Shadows.elevation5};
        `),
        derivedState.isHovered &&
            withModifier('hovered', css `
          box-shadow: ${Shadows.elevation10};
        `),
    ],
    summary: (derivedState) => [
        css `
      ${Typography.paragraph1ShortBold};
      display: block;
      color: ${Colors.neutral700};
      cursor: pointer;
      list-style: none;
      padding: 20px;
      padding-${derivedState.toggleSide}: 55px;

      &::-webkit-details-marker {
        display: none;
      }

      // Remove marker from browser / polyfills
      &::before {
        content: '';
      }
    `,
        derivedState.isOpen &&
            withModifier('open', css `
          // swapping padding for bottom so that when closed, you can tap anywhere
          // to open, after which the margin absorbs any (smaller) content margins
          padding-bottom: 0;
          margin-bottom: 20px;
          color: ${Colors.neutral800};
        `),
        (derivedState.isFocused || derivedState.isHovered) &&
            withModifier('highlighted', css `
          color: ${Colors.blue};
        `),
    ],
    content: (derivedState) => css `
    ${Typography.paragraph1};
    color: ${Colors.neutral800};
    margin: 20px;
    margin-${derivedState.toggleSide}: 55px;
`,
    arrow: (derivedState) => css `
    position: absolute;
    top: 20px;
    ${derivedState.toggleSide}: 15px;
  `,
    fullPageButton: css `
    margin: 20px;
  `,
});
const FullPageButton = (props) => {
    const iconName = 'arrowOffsite';
    return (isolateComponent("div", { css: Styles.fullPageButton },
        isolateComponent(Button, { color: "secondary", href: props.href, iconBefore: iconName, isFullWidth: true, size: "small", target: "_blank" }, "Open full page")));
};
export const Accordion = (props) => {
    const { children, href, isInitiallyOpen, title, onToggle = () => { }, toggleSide = 'left', } = props;
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isOpen, setIsOpen] = useState(!!isInitiallyOpen);
    const derivedState = {
        isFocused,
        isHovered,
        isOpen,
        toggleSide,
    };
    const iconColor = !isFocused && !isHovered ? 'neutral600' : 'currentColor';
    useEffect(() => {
        if (props.isOpen !== undefined) {
            setIsOpen(props.isOpen);
        }
    }, [props.isOpen]);
    const handleClick = (e) => {
        e.preventDefault();
        onToggle({ isOpen: !isOpen });
        if (props.isOpen === undefined) {
            setIsOpen(!isOpen);
        }
    };
    return (isolateComponent("details", { css: Styles.root(derivedState), open: isOpen },
        isolateComponent("summary", { css: Styles.summary(derivedState), onBlur: () => setIsFocused(false), onClick: handleClick, onFocus: () => setIsFocused(true), onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), tabIndex: 0, role: "button", "aria-expanded": isOpen },
            isolateComponent("span", { css: Styles.arrow(derivedState) },
                isolateComponent(Icon, { canAcceptPointerEvents: false, color: iconColor, name: isOpen ? 'arrowSmallUp' : 'arrowSmallDown', size: 24 })),
            title),
        isOpen && isolateComponent("div", { css: Styles.content(derivedState) }, children),
        isOpen && href && isolateComponent(FullPageButton, Object.assign({}, props))));
};
//# sourceMappingURL=Accordion.js.map
// @zapier/design-system@6.28.0 (14d5b99c8)
